import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ApiFilterOperation,
  DetailContext,
  DetailHandle,
  DetailMode,
  EvidenceContext,
  FormAutocomplete,
  FormCheckbox,
  FormDateField,
  FormDateTimeField,
  FormLocalDateTimeField,
  FormPanel,
  FormSelect,
  FormTextField,
  eqFilterParams,
  useStaticListSource,
} from '@eas/common-web';
import { usePhysicalStorages } from '@modules/dict-physical-storage/dict-physical-storage-api';
import { RunningDeadlineField } from '@composite/deadlines/running-deadline-field';
import { NoteField } from '@composite/notes/note-field';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { EsFile } from '@models';
import { Agenda, Role, UIElement } from '@enums';
import { useSettlementMethods } from '../../../dict-settlement-method/dict-settlement-method-api';
import { NOTE_SOURCE } from '../../../es-file/es-file-hooked-sources';
import { useInfFilePermission } from '../../../es-file/es-file-permission';
import { useUserRoles } from '../../../role/role-api';

export function GeneralFields() {
  const { agendas, states } = useContext(StaticDataContext);
  const { hookedSources } = useContext(EvidenceContext);
  const { isLocked, mode, source } =
    useContext<DetailHandle<EsFile>>(DetailContext);

  const solverUsers = useUserRoles({
    filters: [
      {
        field: 'role.id',
        operation: ApiFilterOperation.EQ,
        value: Role.SOLVER,
      },
      {
        field: 'agendas.id',
        operation: ApiFilterOperation.EQ,
        value: Agenda.INF,
      },
    ],
  });
  const indexUsers = useUserRoles({
    filters: [
      {
        field: 'role.id',
        operation: ApiFilterOperation.EQ,
        value: Role.INDEX,
      },
      {
        field: 'agendas.id',
        operation: ApiFilterOperation.EQ,
        value: Agenda.INF,
      },
    ],
  });
  const agendaSource = useStaticListSource(agendas);
  const stateSource = useStaticListSource(states);
  const settlementMethods = useSettlementMethods(
    eqFilterParams({ field: 'agenda.id', value: Agenda.INF })
  );
  const physicalStorages = usePhysicalStorages();

  const disabledNotes = isLocked || mode !== DetailMode.VIEW;
  const noteSource = hookedSources[NOTE_SOURCE]?.source;

  /**
   * Permissions.
   */
  const { hasPermission } = useInfFilePermission(source.data);

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__INF_FILES__DETAIL__GENERAL_PANEL_TITLE"
            defaultMessage="Spis"
          />
        }
      >
        <FormTextField
          name="number.value"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__FILE_NUMBER"
              defaultMessage="Spisová značka"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.FILE_NUMBER)}
        />
        <FormAutocomplete
          name="physicalStorage"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__PHYSICAL_STORAGE"
              defaultMessage="Fyzické uložení"
            />
          }
          source={physicalStorages}
          freeSolo={true}
          labelMapper={autocompleteLabelMapper}
          disabled={!hasPermission(UIElement.InfFile.PHYSICAL_STORAGE)}
        />
        <FormTextField
          name="incomingNumber"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__INCOMING_NUMBER"
              defaultMessage="Došlé číslo"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.INCOMING_NUMBER)}
        />
        <FormTextField
          name="jid"
          label={
            <FormattedMessage
              id="ES__COURT_FILES__DETAIL__FIELD_LABEL__JID"
              defaultMessage="JID"
            />
          }
          disabled={true}
        />
        <FormDateTimeField
          name="created"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__CREATED"
              defaultMessage="Datum vytvoření"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.CREATED)}
        />
        <FormDateField
          name="deliveryDate"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__DELIVERY_DATE"
              defaultMessage="Datum doručení"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.DELIVERY_DATE)}
        />
        <FormSelect
          name="agenda"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__AGENDA"
              defaultMessage="Agenda"
            />
          }
          required
          source={agendaSource}
          disabled={!hasPermission(UIElement.InfFile.AGENDA)}
          valueIsId={true}
        />
        <FormSelect
          name="state"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__STATE"
              defaultMessage="Stav"
            />
          }
          source={stateSource}
          tooltipMapper={(o) => o.name}
          disabled={!hasPermission(UIElement.InfFile.STATE)}
          valueIsId={true}
        />
        <FormAutocomplete
          name="indexUserRole"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__INDEX_USER_ROLE"
              defaultMessage="Vedoucí rejstříku"
            />
          }
          source={indexUsers}
          labelMapper={autocompleteLabelMapper}
          disabled={!hasPermission(UIElement.InfFile.INDEX_USER_ROLE)}
        />
        <FormAutocomplete
          name="solverUserRoles"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__SOLVER_USER_ROLES"
              defaultMessage="Řešitelé"
            />
          }
          source={solverUsers}
          labelMapper={autocompleteLabelMapper}
          multiple={true}
          disabled={!hasPermission(UIElement.InfFile.SOLVER_USER_ROLES)}
        />
        <FormCheckbox
          name="administrativeDecision"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__ADMINISTRATIVE_DECISION"
              defaultMessage="Správní rozhodnutí"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.ADMINISTRATIVE_DECISION)}
        />
        <FormCheckbox
          name="complaint"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__COMPLAINT"
              defaultMessage="Stížnost"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.COMPLAINT)}
        />
        <FormAutocomplete
          name="settlementMethods"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__SETTLEMENT_METHODS"
              defaultMessage="Způsob vyřízení"
            />
          }
          source={settlementMethods}
          multiple={true}
          labelMapper={autocompleteLabelMapper}
          disabled={!hasPermission(UIElement.InfFile.SETTLEMENT_METHODS)}
        />
        <FormTextField
          name="barCode"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__BAR_CODE"
              defaultMessage="Čárový kód"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.BAR_CODE)}
        />
        <FormDateField
          name="settlementDate"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__SETTLEMENT_DATE"
              defaultMessage="Datum vyřízení"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.SETTLEMENT_DATE)}
        />
        <FormLocalDateTimeField
          name="closingDate"
          label={
            <FormattedMessage
              id="ES__INF_FILES__DETAIL__FIELD_LABEL__CLOSING_DATE"
              defaultMessage="Datum uzavření"
            />
          }
          disabled={!hasPermission(UIElement.InfFile.CLOSING_DATE)}
        />
      </FormPanel>
      {mode !== DetailMode.NEW && (
        <RunningDeadlineField
          disabled={!hasPermission(UIElement.InfFile.RUNNING_DEADLINES)}
        />
      )}
      <NoteField
        disabled={disabledNotes || !hasPermission(UIElement.InfFile.NOTES)}
        source={noteSource}
      />
    </>
  );
}
